import React, {
  Fragment,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import injectStyles from 'react-jss'

import Grid from '@material-ui/core/Grid'
import { GridItem } from 'Components/Page/StatsPageWrapper'
import { formatData, getStats, renderMailSpeedGraphPopup } from './utils'
import { chartOptions } from './options'
import ChartHeader from '../ChartHeader'
import Loader from '../../../StatsOverview/components/HighchartsCharts/components/Loader'
import Stats from './Stats'
import {
  filterDataByRange,
  getDifferences,
  getParametersFilters,
  numberToThousand,
} from '../utils'
import { styles } from './styles'

const Chart = React.lazy(() => import('Components/Chart'))

const MailSpeedGraph = ({
  deliverabilityReport: { nodes: reportData },
  classes,
}) => {
  const data = useMemo(() => formatData(reportData), [reportData])
  const xAxisDays = Object.keys(data.resultsByDate)
  const [options, setOptions] = useState(chartOptions)
  const [form, setForm] = useState({
    from: xAxisDays[0],
    to: xAxisDays[xAxisDays.length - 1],
  })
  const [rangeDatesList, setRangeDatesList] = useState(
    xAxisDays.slice(
      xAxisDays.indexOf(form.from),
      xAxisDays.indexOf(form.to) + 1,
    ),
  )

  useEffect(() => {
    setRangeDatesList(
      xAxisDays.slice(
        xAxisDays.indexOf(form.from),
        xAxisDays.indexOf(form.to) + 1,
      ),
    )
  }, [form.from, form.to])

  const filteredDataByRange = filterDataByRange(
    rangeDatesList,
    data.resultsByDate,
  )
  const filteredDataByRangeByDifference = rangeDatesList.slice(1)

  const differences = getDifferences(filteredDataByRange)

  const { average, median } = getStats(differences, true)

  useEffect(() => {
    setOptions(prev => ({
      ...prev,
      xAxis: {
        categories: filteredDataByRangeByDifference,
      },
      series: [
        {
          data: differences,
        },
      ],
      tooltip: {
        useHTML: true,
        formatter: function() {
          const totalMailedPercent = Math.trunc(
            (this.y / data.totalMailed) * 100,
          )
          const isValueThousand = this.y / 1000 >= 1
          return renderMailSpeedGraphPopup({
            date: this.key,
            delivered: isValueThousand ? numberToThousand(this.y) : this.y,
            totalMailedPercent,
          })
        },
      },
    }))
  }, [rangeDatesList])

  const { from, to } = getParametersFilters({
    filterOptions: xAxisDays,
    formFrom: form.from,
    formTo: form.to,
  })

  const handleFormChange = useCallback(item => {
    if (item.isDirty) {
      setForm(prev => ({ ...prev, [item.name]: item.value }))
    }
  }, [])

  const distributedByThisDate = options.series
    ? options.series[0].data.reduce((a, b) => a + b, 0)
    : 0

  return (
    <div>
      <h1>Mail speed</h1>
      <GridItem>
        <Suspense fallback={<Loader />}>
          <Grid container>
            <Grid item xs={12}>
              {Object.keys(data.resultsByDate).length ? (
                <Fragment>
                  <ChartHeader
                    form={form}
                    handleFormChange={handleFormChange}
                    from={from}
                    to={to}
                  />
                  <Stats
                    data={data}
                    distributedByThisDate={distributedByThisDate}
                    average={average}
                    median={median}
                  />
                </Fragment>
              ) : null}
            </Grid>
            <Grid item xs={12}>
              <Chart options={options} />
            </Grid>
          </Grid>
        </Suspense>
      </GridItem>
      <div className={classes.footnote}>
        *Postage Velocity is the average number of flyers posted per day
      </div>
    </div>
  )
}

export default injectStyles(styles)(MailSpeedGraph)
